import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button, Input, Label, Textarea } from "@/components/ui/primitives";
import { toast } from "@/components/ui/toaster";
import {
  createCapability,
  listMyDomains,
  updateCapability,
  type CapabilityInput,
} from "@/lib/api/endpoints";
import { useMyAgents, useTrustedSystems, useVersions } from "@/lib/query/hooks";
import { qk } from "@/lib/query/keys";
import type { CapabilityView, ExecutionType, RiskTier } from "@/types/api";

import {
  agentReferenceConfig,
  DEFAULT_AGENT_REFERENCE,
  parseAgentReference,
  type AgentReference,
} from "./agent-reference";

const selectClass =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

const EXECUTION_TYPES: ExecutionType[] = ["mcp", "http", "client", "agent"];
const RISK_TIERS: RiskTier[] = ["read", "sensitive_read", "write", "external"];

/** Create (capability undefined) or edit a capability / MCP tool. */
/** What each auth mode actually sends, in one line each.
 *
 * The default is the right answer for a server you control; pass-through
 * exists for one you do not, or one that already verifies your own tokens and
 * that you would rather not reconfigure (ADR-020/021). */
const AUTH_HINT: Record<string, string> = {
  agent_foundry_jwt:
    "Aviary signs a fresh short-lived token per call. The server must trust Aviary's JWKS.",
  static_token: "One stored credential. The server authenticates Aviary, not the end user.",
  passthrough_inbound:
    "The caller's own token, unchanged. For a server that already verifies your tokens.",
};

export function CapabilityDialog({
  open,
  onOpenChange,
  capability,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  capability?: CapabilityView;
}) {
  const qc = useQueryClient();
  const editing = !!capability;
  const domains = useQuery({ queryKey: qk.myDomains, queryFn: listMyDomains, enabled: open });

  const [name, setName] = React.useState("");
  const [domainSlug, setDomainSlug] = React.useState("");
  const [execType, setExecType] = React.useState<ExecutionType>("mcp");
  const [riskTier, setRiskTier] = React.useState<RiskTier>("read");
  const [description, setDescription] = React.useState("");
  // config fields
  const [serverUrl, setServerUrl] = React.useState("");
  const [authType, setAuthType] = React.useState("static_token");
  // Which registered calling application's token a pass-through tool accepts.
  // Bound by issuer rather than row id: it needs no lookup at dispatch, and
  // an identifier is immutable so the binding cannot drift (ADR-021).
  const [trustedIssuer, setTrustedIssuer] = React.useState("");
  const trustedSystems = useTrustedSystems();
  const [secretToken, setSecretToken] = React.useState("");
  const [clearSecret, setClearSecret] = React.useState(false);
  const [httpUrl, setHttpUrl] = React.useState("");
  const [httpMethod, setHttpMethod] = React.useState("POST");
  const [agentRef, setAgentRef] = React.useState<AgentReference>(DEFAULT_AGENT_REFERENCE);
  const agents = useMyAgents();
  const targetSlug = agents.data?.find((a) => a.id === agentRef.agentId)?.slug ?? "";
  const targetVersions = useVersions(execType === "agent" ? targetSlug : "");

  React.useEffect(() => {
    if (!open) return;
    setName(capability?.name ?? "");
    setDomainSlug(capability?.domain_slug ?? "");
    setExecType((capability?.execution_type as ExecutionType) ?? "mcp");
    setRiskTier((capability?.risk_tier as RiskTier) ?? "read");
    setDescription(capability?.description ?? "");
    const cfg = (capability?.execution_config ?? {}) as Record<string, unknown>;
    setServerUrl(typeof cfg.server_url === "string" ? cfg.server_url : "");
    setAuthType(typeof cfg.auth_type === "string" ? cfg.auth_type : "static_token");
    setTrustedIssuer(
      typeof cfg.trusted_system_issuer === "string" ? cfg.trusted_system_issuer : "",
    );
    setSecretToken("");
    setClearSecret(false);
    setHttpUrl(typeof cfg.url === "string" ? cfg.url : "");
    setHttpMethod(typeof cfg.method === "string" ? cfg.method : "POST");
    setAgentRef(
      capability?.execution_type === "agent" ? parseAgentReference(cfg) : DEFAULT_AGENT_REFERENCE,
    );
  }, [open, capability]);

  React.useEffect(() => {
    const first = domains.data?.[0];
    if (open && !editing && !domainSlug && first) setDomainSlug(first.slug);
  }, [open, editing, domainSlug, domains.data]);

  const buildConfig = (): Record<string, unknown> => {
    if (execType === "mcp") {
      const cfg: Record<string, unknown> = { server_url: serverUrl, auth_type: authType };
      // Only meaningful for pass-through; sending it otherwise would leave a
      // stale binding behind when someone switches auth mode.
      if (authType === "passthrough_inbound") cfg.trusted_system_issuer = trustedIssuer;
      return cfg;
    }
    if (execType === "http") return { url: httpUrl, method: httpMethod };
    if (execType === "client") return {};
    // agent
    const built = agentReferenceConfig(agentRef);
    if ("error" in built) throw new Error(built.error);
    return built.config;
  };

  const mutation = useMutation({
    mutationFn: () => {
      const execution_config = buildConfig();
      const body: CapabilityInput = {
        name,
        description: description || null,
        execution_type: execType,
        execution_config,
        risk_tier: riskTier,
      };
      // MCP static-token credential: send a value to (re)set, null to clear.
      if (execType === "mcp" && authType === "static_token") {
        if (clearSecret) body.secret_token = null;
        else if (secretToken.trim()) body.secret_token = secretToken.trim();
      }
      return editing
        ? updateCapability(capability!.tool_name, body)
        : createCapability({ ...body, domain_slug: domainSlug });
    },
    onSuccess: async () => {
      toast.success(editing ? "Tool updated." : "Tool created.");
      await qc.invalidateQueries({ queryKey: qk.capabilities });
      onOpenChange(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const noDomains = domains.data?.length === 0;
  const canSubmit = !!name.trim() && (editing || !!domainSlug) && !mutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{editing ? `Edit tool “${capability?.name}”` : "New tool"}</DialogTitle>
          <DialogDescription>
            A capability is a tool an agent can call — an MCP server, an HTTP endpoint, a
            client-side action, or a delegated agent.
          </DialogDescription>
        </DialogHeader>
        {noDomains && !editing ? (
          <p className="text-sm text-muted-foreground">Create a domain first.</p>
        ) : (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (canSubmit) mutation.mutate();
            }}
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="c-name">Name</Label>
                <Input
                  id="c-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="FAM Search"
                  autoFocus
                />
              </div>
              {!editing ? (
                <div className="space-y-1.5">
                  <Label htmlFor="c-domain">Domain</Label>
                  <select
                    id="c-domain"
                    className={selectClass}
                    value={domainSlug}
                    onChange={(e) => setDomainSlug(e.target.value)}
                  >
                    {(domains.data ?? []).map((d) => (
                      <option key={d.slug} value={d.slug}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="c-type">Execution type</Label>
                <select
                  id="c-type"
                  className={selectClass}
                  value={execType}
                  onChange={(e) => setExecType(e.target.value as ExecutionType)}
                >
                  {EXECUTION_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="c-risk">Risk tier</Label>
                <select
                  id="c-risk"
                  className={selectClass}
                  value={riskTier}
                  onChange={(e) => setRiskTier(e.target.value as RiskTier)}
                >
                  {RISK_TIERS.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {execType === "mcp" ? (
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="c-server">MCP server URL</Label>
                  <Input
                    id="c-server"
                    value={serverUrl}
                    onChange={(e) => setServerUrl(e.target.value)}
                    placeholder="https://mcp.example/rpc"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="c-auth">Auth</Label>
                  <select
                    id="c-auth"
                    className={selectClass}
                    value={authType}
                    onChange={(e) => setAuthType(e.target.value)}
                  >
                    <option value="agent_foundry_jwt">Aviary-signed JWT</option>
                    <option value="static_token">Stored token</option>
                    <option value="passthrough_inbound">Forward the caller&rsquo;s token</option>
                  </select>
                  <p className="text-xs text-muted-foreground">{AUTH_HINT[authType]}</p>
                </div>
              </div>
            ) : null}

            {execType === "mcp" && authType === "passthrough_inbound" ? (
              <div className="space-y-1.5">
                <Label htmlFor="c-trusted">Token from</Label>
                <select
                  id="c-trusted"
                  className={selectClass}
                  value={trustedIssuer}
                  onChange={(e) => setTrustedIssuer(e.target.value)}
                >
                  <option value="">Select a calling application…</option>
                  {(trustedSystems.data ?? []).map((t) => (
                    <option key={t.id} value={t.identifier}>
                      {t.name}
                    </option>
                  ))}
                </select>
                {/* The binding is the guard: without it, the model calling this
                    tool could hand another integration's credential to this
                    server. It is also why revoking a registration stops its
                    tools rather than loosening them. */}
                <p className="text-xs text-muted-foreground">
                  This tool fires only for callers from this application, and receives their token
                  unchanged — so a server that already verifies your tokens needs no change.{" "}
                  {(trustedSystems.data ?? []).length === 0
                    ? "Register one under Trusted systems first."
                    : null}
                </p>
              </div>
            ) : null}

            {execType === "mcp" && authType === "static_token" ? (
              <div className="space-y-1.5">
                <Label htmlFor="c-token">Server credential (bearer token)</Label>
                <Input
                  id="c-token"
                  type="password"
                  value={secretToken}
                  onChange={(e) => {
                    setSecretToken(e.target.value);
                    setClearSecret(false);
                  }}
                  placeholder={
                    editing && capability?.secret_configured
                      ? "•••• stored — enter a new value to replace"
                      : "the MCP server's API key / token"
                  }
                  autoComplete="off"
                />
                <p className="text-xs text-muted-foreground">
                  Stored encrypted; sent as <code className="font-mono">Authorization: Bearer</code>{" "}
                  to the MCP server.
                  {editing && capability?.secret_configured ? (
                    <>
                      {" "}
                      A credential is stored.{" "}
                      <button
                        type="button"
                        className="text-primary underline"
                        onClick={() => {
                          setClearSecret(true);
                          setSecretToken("");
                        }}
                      >
                        Clear it
                      </button>
                    </>
                  ) : null}
                </p>
                {clearSecret ? (
                  <p className="text-xs text-destructive">
                    The stored credential will be removed on save.
                  </p>
                ) : null}
              </div>
            ) : null}

            {execType === "http" ? (
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="c-url">HTTP URL</Label>
                  <Input
                    id="c-url"
                    value={httpUrl}
                    onChange={(e) => setHttpUrl(e.target.value)}
                    placeholder="https://api.example/do"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="c-method">Method</Label>
                  <Input
                    id="c-method"
                    value={httpMethod}
                    onChange={(e) => setHttpMethod(e.target.value)}
                    placeholder="POST"
                  />
                </div>
              </div>
            ) : null}

            {execType === "agent" ? (
              <div className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="c-agent">Agent</Label>
                    <select
                      id="c-agent"
                      className={selectClass}
                      value={agentRef.agentId ?? ""}
                      onChange={(e) => {
                        const agentId = e.target.value ? Number(e.target.value) : null;
                        // A pinned version belongs to the old agent; drop it.
                        setAgentRef((r) =>
                          r.mode === "version"
                            ? { mode: "version", agentId, versionId: null }
                            : { ...r, agentId },
                        );
                      }}
                    >
                      <option value="">Select an agent…</option>
                      {(agents.data ?? []).map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="c-ref">Which version</Label>
                    <select
                      id="c-ref"
                      className={selectClass}
                      value={agentRef.mode}
                      onChange={(e) =>
                        setAgentRef((r) =>
                          e.target.value === "version"
                            ? { mode: "version", agentId: r.agentId, versionId: null }
                            : { mode: "tag", agentId: r.agentId, tag: "latest" },
                        )
                      }
                    >
                      <option value="tag">By tag (e.g. latest)</option>
                      <option value="version">A specific version</option>
                    </select>
                  </div>
                </div>
                {agentRef.mode === "tag" ? (
                  <div className="space-y-1.5">
                    <Label htmlFor="c-tag">Tag</Label>
                    <Input
                      id="c-tag"
                      list="c-tag-options"
                      value={agentRef.tag}
                      onChange={(e) => setAgentRef({ ...agentRef, tag: e.target.value })}
                      placeholder="latest"
                    />
                    <datalist id="c-tag-options">
                      {[
                        ...new Set((targetVersions.data ?? []).map((v) => v.tag).filter(Boolean)),
                      ].map((tag) => (
                        <option key={tag} value={tag ?? ""} />
                      ))}
                    </datalist>
                    <p className="text-xs text-muted-foreground">
                      Resolved on every call, so moving the tag to a new version updates this tool.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    <Label htmlFor="c-version">Version</Label>
                    <select
                      id="c-version"
                      className={selectClass}
                      value={agentRef.versionId ?? ""}
                      disabled={!targetSlug}
                      onChange={(e) =>
                        setAgentRef({
                          mode: "version",
                          agentId: agentRef.agentId,
                          versionId: e.target.value ? Number(e.target.value) : null,
                        })
                      }
                    >
                      <option value="">
                        {targetSlug ? "Select a version…" : "Select an agent first"}
                      </option>
                      {(targetVersions.data ?? []).map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.version}
                          {v.tag ? ` (${v.tag})` : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  The agent gets only the message your agent writes for it — not the conversation —
                  and runs on its own model, tools and validators. Its answer comes back as the tool
                  result, and its run is logged separately, linked to the calling run.
                </p>
              </div>
            ) : null}

            <div className="space-y-1.5">
              <Label htmlFor="c-desc">Description</Label>
              <Textarea
                id="c-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                placeholder="What the LLM is told this tool does (optional)"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={!canSubmit}>
                {mutation.isPending ? "Saving…" : editing ? "Save" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
